<template>
  <v-container style="max-width:100vw;">
    <v-toolbar flat class="px-4 pt-3">
      <v-row class="ma-0 pt-6">
        <v-toolbar-title>
          Ticket promedio mensual
        </v-toolbar-title>
        <v-spacer/>
        <v-select
          v-model="selectedUserId"
          :items="users"
          item-text="name"
          item-value="id"
          label="Usuario"
          clearable
          dense
          outlined
          rounded
          style="max-width: 260px"
          class="mr-3"
        />
        <v-menu
          v-model="menu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              rounded
              outlined
              dense
              v-model="dateRangeText"
              label="Rango de fechas"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              style="max-width: 320px"
            />
          </template>

          <v-card>
            <v-card-text class="pt-4 pb-0">
              <div class="text-caption mb-2">Desde</div>
              <v-date-picker
                v-model="startMonth"
                type="month"
                no-title
                scrollable
              />
              <div class="text-caption mt-4 mb-2">Hasta</div>
              <v-date-picker
                v-model="endMonth"
                type="month"
                no-title
                scrollable
              />
            </v-card-text>
            <v-card-actions class="pt-0 mt-0">
              <v-spacer />
              <v-btn small text @click="menu = false">Cerrar</v-btn>
              <v-btn small rounded class="elevation-0" color="primary" @click="applyFilter">Aplicar</v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
      </v-row>
    </v-toolbar>

    <div style="background:white!important;" class="px-4 pb-4">
      <apexchart
        v-if="series.length"
        type="bar"
        height="520"
        :options="chartOptions"
        :series="series"
      />

      <v-data-table
        :headers="headers"
        :items="rowsMeta"
        :loading="loading"
        class="mt-4"
        dense
      >
        <template v-slot:item.month="{ item }">
          {{ formatMonthLabel(item.month) }}
        </template>
        <template v-slot:item.total_amount="{ item }">
          {{ money(item.total_amount) }}
        </template>
        <template v-slot:item.avg_ticket="{ item }">
          {{ money(item.avg_ticket) }}
        </template>
        <template v-slot:body.append>
          <tr style="font-weight:600;">
            <td>Totales</td>
            <td>{{ totals.sales_count }}</td>
            <td>{{ money(totals.total_amount) }}</td>
            <td>{{ money(totals.avg_ticket) }}</td>
          </tr>
        </template>
      </v-data-table>
    </div>
  </v-container>
</template>

<script>
import axios from "axios"

export default {
  data() {
    return {
      loading: false,
      menu: false,
      startMonth: undefined,
      endMonth: undefined,
      selectedUserId: null,
      report: null,
      rowsMeta: [],
      headers: [
        { text: "Mes", value: "month" },
        { text: "Cantidad de ventas", value: "sales_count" },
        { text: "Total vendido", value: "total_amount" },
        { text: "Ticket promedio", value: "avg_ticket" }
      ],
      series: [],
      chartOptions: {
        chart: {
          type: "bar",
          toolbar: { show: false },
          zoom: { enabled: false }
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: "45%"
          }
        },
        dataLabels: { enabled: false },
        colors: ["#2563eb"],
        xaxis: {
          categories: [],
          labels: {
            rotate: -45
          }
        },
        yaxis: {
          labels: {
            formatter: (val) => {
              return new Intl.NumberFormat("es-MX", {
                style: "currency",
                currency: "MXN",
                maximumFractionDigits: 0
              }).format(val || 0)
            }
          }
        },
        tooltip: {
          custom: ({ dataPointIndex }) => {
            const row = this.rowsMeta[dataPointIndex]
            if (!row) return ""

            const avg = new Intl.NumberFormat("es-MX", {
              style: "currency",
              currency: "MXN"
            }).format(Number(row.avg_ticket || 0))

            const total = new Intl.NumberFormat("es-MX", {
              style: "currency",
              currency: "MXN"
            }).format(Number(row.total_amount || 0))

            return `
              <div style="padding:10px; min-width: 220px;">
                <div style="font-weight:600; margin-bottom:6px;">
                  ${row.month}
                </div>
                <div style="display:flex; justify-content:space-between; gap:12px;">
                  <span>Ticket promedio</span>
                  <span style="font-weight:600;">${avg}</span>
                </div>
                <div style="display:flex; justify-content:space-between; gap:12px;">
                  <span>Total vendido</span>
                  <span style="font-weight:600;">${total}</span>
                </div>
                <div style="display:flex; justify-content:space-between; gap:12px;">
                  <span>Cantidad de ventas</span>
                  <span style="font-weight:600;">${row.sales_count}</span>
                </div>
              </div>
            `
          }
        }
      }
    }
  },

  computed: {
    users() {
      return (this.$store.state.user.users || [])
        .map((user) => ({
          id: user.id,
          name: `${user.name || ""} ${user.last || ""}`.trim()
        }))
        .sort((a, b) => a.name.localeCompare(b.name, "es"))
    },
    dateRangeText() {
      if (!this.startMonth && !this.endMonth) return ""
      if (this.startMonth && !this.endMonth) return this.formatMonthLabel(this.startMonth)
      return `${this.formatMonthLabel(this.startMonth)} - ${this.formatMonthLabel(this.endMonth)}`
    },
    totals() {
      const base = this.rowsMeta.reduce((acc, row) => {
        acc.sales_count += Number(row.sales_count || 0)
        acc.total_amount += Number(row.total_amount || 0)
        return acc
      }, {
        sales_count: 0,
        total_amount: 0
      })

      return {
        ...base,
        avg_ticket: base.sales_count > 0 ? (base.total_amount / base.sales_count) : 0
      }
    }
  },

  created() {
    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonth = String(now.getMonth() + 1).padStart(2, "0")
    this.startMonth = `${currentYear}-01`
    this.endMonth = `${currentYear}-${currentMonth}`

    this.$store.dispatch("user/getUsers")
    this.fetchReport()
  },

  watch: {
    selectedUserId() {
      this.fetchReport()
    }
  },

  methods: {
    money(value) {
      return new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency: "MXN"
      }).format(Number(value || 0))
    },

    formatMonthLabel(monthKey) {
      if (!monthKey) return ""
      const [year, month] = String(monthKey).split("-")
      const date = new Date(Number(year), Number(month) - 1, 1)
      return date.toLocaleString("es-MX", { month: "long", year: "numeric" })
    },

    monthToDateRange(monthKey, isEnd = false) {
      if (!monthKey) return ""
      const [year, month] = String(monthKey).split("-")
      const y = Number(year)
      const m = Number(month)
      if (isEnd) {
        const lastDay = new Date(y, m, 0).getDate()
        return `${year}-${month}-${String(lastDay).padStart(2, "0")}`
      }
      return `${year}-${month}-01`
    },

    applyFilter() {
      this.menu = false
      this.fetchReport()
    },

    async fetchReport() {
      const start = this.monthToDateRange(this.startMonth, false)
      const end = this.monthToDateRange(this.endMonth, true)
      if (!start || !end) return

      this.loading = true
      try {
        const url =
          process.env.VUE_APP_BACKEND_ROUTE +
          "api/v2/reports/monthly-average-sales-ticket-without-sundays"

        const { data } = await axios.get(url, {
          params: {
            "filter[date_between]": `${start},${end}`,
            ...(this.selectedUserId ? { "filter[user_id]": this.selectedUserId } : {})
          }
        })

        this.report = data
        this.rowsMeta = data?.data || []
        this.chartOptions.xaxis.categories = this.rowsMeta.map((row) => this.formatMonthLabel(row.month))
        this.series = [
          {
            name: "Ticket promedio",
            data: this.rowsMeta.map((row) => Number(row.avg_ticket || 0))
          }
        ]
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
